/**
 * InactivityLogout, silent watchdog that signs the user out after a
 * configurable idle window with a 60-second toast warning.
 *
 * • Listens for: mousemove, mousedown, keydown, scroll, touchstart, click, wheel
 * • Defaults: 15-minute timeout, 60-second warning. Override via the
 *   /api/config/inactivity backend endpoint (admin-tunable).
 * • Mounted once at the top of <App> so it covers every route. No-ops when the
 *   user is not authenticated.
 * • Pure JS, no extra dependencies. Uses sonner toasts which are already in
 *   the project.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const STORAGE_KEY = 'plp_last_activity';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];

const COPY = {
  en: {
    warningTitle: 'You will be logged out in 60 seconds',
    warningBody: 'Move your mouse, scroll, or tap anywhere to stay signed in.',
    stayBtn: 'Stay signed in',
    loggedOut: 'You have been signed out due to inactivity.',
  },
  es: {
    warningTitle: 'Se cerrará tu sesión en 60 segundos',
    warningBody: 'Mueve el ratón, desplázate o toca la pantalla para continuar.',
    stayBtn: 'Mantener sesión',
    loggedOut: 'Tu sesión se ha cerrado por inactividad.',
  },
  ru: {
    warningTitle: 'Сессия завершится через 60 секунд',
    warningBody: 'Двигайте мышь, прокрутите или коснитесь экрана, чтобы остаться в системе.',
    stayBtn: 'Остаться в системе',
    loggedOut: 'Сессия завершена из-за неактивности.',
  },
};

const InactivityLogout = () => {
  const { isAuthenticated, logout } = useAuth();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();

  // Fall back to safe defaults; refreshed from the backend on mount.
  const [timeoutMs, setTimeoutMs] = useState(15 * 60 * 1000);
  const [warningMs, setWarningMs] = useState(60 * 1000);

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const warningToastIdRef = useRef(null);

  const labels = COPY[currentLanguage] || COPY.en;

  // Pull configurable values from the backend (admin can change them)
  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/config/inactivity`;
    axios.get(url).then((res) => {
      if (cancelled || !res?.data) return;
      const t = Number(res.data.timeout_seconds);
      const w = Number(res.data.warning_seconds);
      if (Number.isFinite(t) && t > 0) setTimeoutMs(t * 1000);
      if (Number.isFinite(w) && w > 0) setWarningMs(w * 1000);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const dismissWarningToast = useCallback(() => {
    if (warningToastIdRef.current !== null) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }
  }, []);

  const performLogout = useCallback(async () => {
    dismissWarningToast();
    try { await logout(); } catch (_) { /* no-op */ }
    toast.error(labels.loggedOut, { duration: 6000 });
    navigate('/');
  }, [logout, labels.loggedOut, navigate, dismissWarningToast]);

  const showWarning = useCallback(() => {
    dismissWarningToast();
    warningToastIdRef.current = toast.warning(labels.warningTitle, {
      description: labels.warningBody,
      duration: warningMs,
      action: {
        label: labels.stayBtn,
        onClick: () => {
          // Activity will reset everything via the listeners; nothing to do here.
        },
      },
    });
  }, [labels, warningMs, dismissWarningToast]);

  const armTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    const warnAt = Math.max(timeoutMs - warningMs, 1000);
    warningTimerRef.current = setTimeout(showWarning, warnAt);
    logoutTimerRef.current = setTimeout(performLogout, timeoutMs);
  }, [timeoutMs, warningMs, showWarning, performLogout]);

  const recordActivity = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
    dismissWarningToast();
    armTimers();
  }, [armTimers, dismissWarningToast]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up any running timers when the user is not logged in.
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      dismissWarningToast();
      return undefined;
    }

    armTimers();

    // Cross-tab sync: if another tab records activity, reset our timers too.
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY) armTimers();
    };
    window.addEventListener('storage', handleStorage);

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, recordActivity, { passive: true }),
    );

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, recordActivity));
      window.removeEventListener('storage', handleStorage);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [isAuthenticated, armTimers, recordActivity, dismissWarningToast]);

  return null;
};

export default InactivityLogout;
